import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import { api, useSEO } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export function PaymentSuccess() {
  useSEO("Order Confirmed");
  const [params] = useSearchParams();
  const { clear } = useCart();
  const sessionId = params.get("session_id");
  const gateway = params.get("gateway");
  const orderQ = params.get("order");
  const [status, setStatus] = useState(gateway === "razorpay" || gateway === "cod" ? "paid" : "checking");
  const [orderNumber, setOrderNumber] = useState(orderQ || "");

  useEffect(() => {
    if (gateway === "razorpay" || gateway === "cod") { clear(); return; }
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); setOrderNumber(data.order_number || ""); clear(); }
        else if (["expired", "failed"].includes(data.payment_status)) setStatus("error");
        else setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
  }, [sessionId, gateway]); // eslint-disable-line

  return (
    <Shell testid="payment-success">
      {status === "checking" && (
        <>
          <Loader2 size={38} className="animate-spin mb-8" />
          <h1 className="font-display text-4xl md:text-6xl tracking-tight mb-4">Confirming payment…</h1>
          <p className="text-muted-foreground">Hold tight while we verify your transaction.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full bg-black text-white flex items-center justify-center mb-8"><Check size={28} /></div>
          <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] mb-5">Order confirmed</h1>
          {orderNumber && <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4" data-testid="success-order-number">Order {orderNumber}</p>}
          <p className="text-muted-foreground max-w-md mb-10">Thank you for choosing VYRON. A confirmation has been sent to your email. Your frames are being prepared with care.</p>
          <div className="flex gap-3">
            {orderNumber && <Link to="/track" className="border border-black rounded-full px-8 py-3.5 uppercase text-xs tracking-[0.2em] hover:bg-black hover:text-white transition-colors">Track order</Link>}
            <Link to="/collections" data-testid="success-continue" className="bg-black text-white rounded-full px-8 py-3.5 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors">Continue shopping</Link>
          </div>
        </>
      )}
      {(status === "error" || status === "timeout") && (
        <>
          <h1 className="font-display text-4xl md:text-6xl tracking-tight mb-4">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="text-muted-foreground max-w-md mb-10">
            {status === "timeout" ? "Your payment is taking longer than expected. Check your email for confirmation." : "We couldn't confirm your payment. If you were charged, please contact our concierge."}
          </p>
          <Link to="/collections" className="border border-black rounded-full px-8 py-3.5 uppercase text-xs tracking-[0.2em] hover:bg-black hover:text-white transition-colors">Back to shop</Link>
        </>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  useSEO("Payment Cancelled");
  return (
    <Shell testid="payment-cancel">
      <div className="w-16 h-16 rounded-full border border-black/25 flex items-center justify-center mb-8"><X size={28} className="text-black/50" /></div>
      <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] mb-5">Payment cancelled</h1>
      <p className="text-muted-foreground max-w-md mb-10">No charge was made. Your bag is saved &mdash; resume whenever you&rsquo;re ready.</p>
      <Link to="/checkout" className="bg-black text-white rounded-full px-8 py-3.5 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors">Return to checkout</Link>
    </Shell>
  );
}

const Shell = ({ children, testid }) => (
  <div data-testid={testid} className="min-h-[80vh] flex flex-col items-center justify-center text-center px-6 pt-24">
    {children}
  </div>
);
